/**
 * Desktop nav item with optional mega-menu sub-panel on hover, as on the
 * corporate header: a parent with children is a button that opens the panel
 * (its first row links the parent page); without children it is a plain link.
 * Links may target in-app routes or absolute URLs (handled by ChromeLink).
 */
import { useState } from 'react';
import { ChromeLink } from './ChromeLink';

export interface NavItem {
  title: string;
  path: string;
  children?: { title: string; path: string }[];
}

interface Props {
  title: string;
  path: string;
  subItems?: { title: string; path: string }[];
  viewAllLabel?: string;
}

export function DesktopMegaItem({
  title,
  path,
  subItems,
  viewAllLabel = 'View all',
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const hasChildren = !!subItems && subItems.length > 0;

  // Plain link (no dropdown).
  if (!hasChildren) {
    return (
      <ChromeLink
        to={path}
        className="text-xs lg:text-sm 2xl:text-base font-medium text-foreground-secondary hover:text-foreground transition-colors duration-150 whitespace-nowrap"
      >
        <span>{title}</span>
      </ChromeLink>
    );
  }

  // Parent button + hover mega-menu (parity with the corporate header).
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="text-xs lg:text-sm 2xl:text-base font-medium text-foreground-secondary hover:text-foreground transition-colors duration-150 flex items-center whitespace-nowrap"
      >
        <span>{title}</span>
        <svg
          className="ml-1 w-3 h-3 shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open ? (
        <div className="absolute top-full left-0 pt-2">
          <div className="bg-white border border-slate-200 rounded-xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.15)] py-2 min-w-[260px]">
            <ChromeLink
              to={path}
              className="block px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-brand-50 transition-colors duration-150 border-b border-border/50 mb-1"
            >
              {viewAllLabel} <span>{title}</span>
            </ChromeLink>
            {subItems!.map((child) => (
              <ChromeLink
                key={child.path}
                to={child.path}
                className="block px-4 py-2.5 text-sm font-medium text-foreground-secondary hover:text-foreground hover:bg-brand-50 transition-colors duration-150"
              >
                <span>{child.title}</span>
              </ChromeLink>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
